/**
 * Calls the homepage makes.
 *
 * Plain `fetch` rather than `@common/axios`: that instance's interceptors depend on the
 * `globalReactFunctions` global the authenticated app sets up, which does not exist on a public page. Same
 * reasoning as `portalAuthService.js` and `registrationService.js`.
 */

async function request(url, {method = 'GET', body} = {}) {
    const response = await fetch(url, {
        method,
        credentials: 'same-origin',
        headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
        },
        body: body ? JSON.stringify(body) : undefined,
    });

    if (!response.ok) {
        throw new Error(`Request failed: ${response.status}`);
    }
    return response.json();
}

const HOME = '/web/ui/home/public';

/** Brand flags that decide what the page renders. Copy stays in the bundle. */
export function fetchConfig() {
    return request(`${HOME}/config`);
}

/**
 * Live prices from the product table.
 *
 * The page this replaces typed $79.95 and $149.95 into inline styles, so a price rise had to be made in
 * two places and the homepage was silently wrong until someone noticed.
 */
export function fetchPricing() {
    return request(`${HOME}/pricing`);
}

/**
 * Ask for a password reset link.
 *
 * `username` is only sent on a second attempt, after the server has answered `usernameRequired` because
 * the address has more than one account.
 *
 * @returns {Promise<{sent: boolean, usernameRequired: boolean, message: string}>}
 */
export function requestPasswordReset({email, username, recaptchaToken}) {
    return request(`${HOME}/resetPasswordEmail`, {
        method: 'POST',
        body: {email, username, recaptchaToken},
    });
}

/** Start a free trial. Rate limited per address server side, because it sends email. */
export function requestFreeTrial({email, recaptchaToken}) {
    return request(`${HOME}/freeTrial`, {method: 'POST', body: {email, recaptchaToken}});
}

/**
 * Sign in.
 *
 * Deliberately a different controller: `BrandAuthController` already handles reCAPTCHA, establishes the
 * session through `userLoginService.loginUser`, and maps every `UserStatus` - external-auth redirects,
 * lockout with a retry time, and the brand's own failure messages. There was no reason to write a second
 * one.
 *
 * @returns {Promise<{loggedIn: boolean, redirectUrl?: string, errorMessage?: string}>} note there is no
 *   `success` field, and a successful sign-in comes back as a `redirectUrl` with `loggedIn` still false -
 *   see BrandLoginResultBean.redirect()
 */
export function login({username, password, recaptchaToken}) {
    return request('/web/ui/brandAuth/public/login', {
        method: 'POST',
        body: {username, password, recaptchaToken},
    });
}

/**
 * Send a message from a marketing homepage's contact form (Edjin) to the brand's inbox. reCAPTCHA checked and rate
 * limited per address and IP server side, because it sends email.
 *
 * @returns {Promise<{sent: boolean, message: string}>}
 */
export function sendContactMessage({name, email, organisation, message, recaptchaToken}) {
    return request(`${HOME}/contact`, {
        method: 'POST',
        body: {name, email, organisation, message, recaptchaToken},
    });
}

/**
 * Send a school's request for a quote to the brand's sales inbox. Rate limited per address and IP server
 * side, because it sends email.
 *
 * @returns {Promise<{sent: boolean, message: string}>}
 */
export function requestSchoolEnquiry({contactName, contactPhone, email, schoolName, schoolAddress, enquiry,
                                         recaptchaToken}) {
    return request(`${HOME}/schoolEnquiry`, {
        method: 'POST',
        body: {contactName, contactPhone, email, schoolName, schoolAddress, enquiry, recaptchaToken},
    });
}
